import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, setDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import Navbar from "./Navbar";

function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [displayName, setDisplayName] = useState("");
  const [learningGoal, setLearningGoal] = useState("");
  const [experience, setExperience] = useState("");
  const [timeCommitment, setTimeCommitment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { currentUser } = useAuth();

  useEffect(() => {
    // Check if user is authenticated
    if (!currentUser) {
      console.log("No authenticated user, redirecting to login");
      navigate("/login");
    }
  }, [currentUser, navigate]);

  const handleComplete = async () => {
    console.log("handleComplete called");
    console.log("Form data:", { displayName, learningGoal, experience, timeCommitment });
    
    setLoading(true);
    setError("");
    
    try {
      if (!currentUser) {
        console.error("No authenticated user found");
        setError("No authenticated user found. Please log in again.");
        return;
      }

      console.log("Creating user document for:", currentUser.uid);
      console.log("User email:", currentUser.email);
      
      const userRef = doc(db, "users", currentUser.uid);
      const userData = {
        email: currentUser.email,
        displayName: displayName,
        learningGoal: learningGoal,
        experience: experience,
        timeCommitment: timeCommitment,
        createdAt: new Date().toISOString(),
        completedLessons: [],
        vocabularyProgress: {},
        quizScores: [],
        studyStreak: 0,
        totalStudyTime: 0,
        preferences: {
          language: "en",
          notifications: true,
          theme: "korean"
        },
        onboardingCompleted: true
      };

      console.log("User data to save:", userData);
      
      await setDoc(userRef, userData);
      console.log("User document created successfully");

      setError(""); // Clear any previous errors
      
      // Navigate immediately without delay
      navigate("/dashboard");
      
    } catch (error) {
      console.error("Error completing onboarding:", error);
      setError(`Error completing setup: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step < 4) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 shadow-2xl border-b-4 border-red-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
            <div className="text-center">
              <div className="flex justify-center mb-6 sm:mb-8">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-red-500 to-orange-500 rounded-full blur-lg opacity-30 animate-pulse"></div>
                  <div className="text-4xl sm:text-6xl relative z-10">🎯</div>
                  <div className="absolute -top-2 -right-2 bg-red-600 text-white text-sm font-bold px-3 py-1 rounded-full animate-bounce">
                    🇰🇷
                  </div>
                </div>
              </div>
              
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-gray-900 mb-4 sm:mb-6">
                Welcome to Korlish!
              </h1>
              <div className="mb-4 sm:mb-6">
                <p className="text-xl sm:text-3xl text-red-700 font-black mb-2">Korlish에 오신 것을 환영합니다!</p>
                <p className="text-sm sm:text-lg text-gray-600 font-semibold">(Welcome to Korlish!)</p>
              </div>
              <p className="text-lg sm:text-2xl text-gray-800 max-w-4xl mx-auto mb-8 sm:mb-10 font-semibold">
                Let's personalize your Korean learning journey
              </p>

              {/* Progress Bar */}
              <div className="w-full bg-gray-300 rounded-full h-4 mb-8 border-2 border-gray-400">
                <div
                  className="bg-gradient-to-r from-red-600 to-orange-600 h-4 rounded-full transition-all duration-500 shadow-lg"
                  style={{ width: `${(step / 4) * 100}%` }}
                ></div>
              </div>
              <p className="text-lg text-gray-700 font-semibold">Step {step} of 4</p>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-2xl p-10 border-4 border-red-200">
            {step === 1 && (
              <div className="text-center">
                <div className="text-6xl mb-8">👋</div>
                <h2 className="text-3xl font-black text-gray-900 mb-6">What should we call you?</h2>
                <p className="text-xl text-gray-700 mb-8 font-semibold">
                  Choose a display name for your profile
                </p>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your display name"
                  className="w-full px-6 py-4 border-4 border-red-200 rounded-2xl focus:ring-4 focus:ring-red-300 focus:border-red-400 transition-colors duration-300 text-gray-900 placeholder-gray-500 text-lg font-semibold bg-white/80 mb-8"
                />
              </div>
            )}

            {step === 2 && (
              <div className="text-center">
                <div className="text-6xl mb-8">🎯</div>
                <h2 className="text-3xl font-black text-gray-900 mb-6">What's your learning goal?</h2>
                <p className="text-xl text-gray-700 mb-8 font-semibold">
                  This helps us personalize your experience
                </p>
                <div className="space-y-4">
                  {[
                    "Travel to Korea",
                    "Connect with heritage",
                    "K-pop & K-drama",
                    "Business & work",
                    "Academic study",
                    "Just for fun"
                  ].map((goal) => (
                    <button
                      key={goal}
                      onClick={() => setLearningGoal(goal)}
                      className={`w-full p-4 rounded-2xl font-bold text-lg transition-all duration-300 border-4 ${
                        learningGoal === goal
                          ? "bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-2xl border-red-300 transform scale-105"
                          : "bg-white text-gray-800 border-red-200 hover:border-red-400 hover:bg-red-50 hover:scale-105"
                      }`}
                    >
                      {goal}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="text-center">
                <div className="text-6xl mb-8">📚</div>
                <h2 className="text-3xl font-black text-gray-900 mb-6">What's your Korean experience?</h2>
                <p className="text-xl text-gray-700 mb-8 font-semibold">
                  This helps us recommend the right starting point
                </p>
                <div className="space-y-4">
                  {[
                    "Complete beginner",
                    "Know some words",
                    "Can read Hangul",
                    "Basic conversations",
                    "Intermediate level"
                  ].map((exp) => (
                    <button
                      key={exp}
                      onClick={() => setExperience(exp)}
                      className={`w-full p-4 rounded-2xl font-bold text-lg transition-all duration-300 border-4 ${
                        experience === exp
                          ? "bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-2xl border-red-300 transform scale-105"
                          : "bg-white text-gray-800 border-red-200 hover:border-red-400 hover:bg-red-50 hover:scale-105"
                      }`}
                    >
                      {exp}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="text-center">
                <div className="text-6xl mb-8">⏰</div>
                <h2 className="text-3xl font-black text-gray-900 mb-6">How much time can you commit?</h2>
                <p className="text-xl text-gray-700 mb-8 font-semibold">
                  We'll help you stay on track with your goals
                </p>
                <div className="space-y-4">
                  {[
                    "5-10 minutes daily",
                    "15-30 minutes daily",
                    "30-60 minutes daily",
                    "1-2 hours daily",
                    "Weekend warrior"
                  ].map((time) => (
                    <button
                      key={time}
                      onClick={() => setTimeCommitment(time)}
                      className={`w-full p-4 rounded-2xl font-bold text-lg transition-all duration-300 border-4 ${
                        timeCommitment === time
                          ? "bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-2xl border-red-300 transform scale-105"
                          : "bg-white text-gray-800 border-red-200 hover:border-red-400 hover:bg-red-50 hover:scale-105"
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                  <p className="text-sm text-blue-800">Setting up your account...</p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-12">
              <button
                onClick={prevStep}
                disabled={step === 1}
                className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 ${
                  step === 1
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-gray-600 text-white hover:bg-gray-700 shadow-lg hover:shadow-xl transform hover:scale-105"
                }`}
              >
                ← Previous
              </button>

              {step < 4 ? (
                <button
                  onClick={nextStep}
                  disabled={
                    (step === 1 && !displayName) ||
                    (step === 2 && !learningGoal) ||
                    (step === 3 && !experience) ||
                    (step === 4 && !timeCommitment)
                  }
                  className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 ${
                    (step === 1 && !displayName) ||
                    (step === 2 && !learningGoal) ||
                    (step === 3 && !experience) ||
                    (step === 4 && !timeCommitment)
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-red-600 to-orange-600 text-white hover:from-red-700 hover:to-orange-700 shadow-lg hover:shadow-xl transform hover:scale-105"
                  }`}
                >
                  Next →
                </button>
              ) : (
                <button
                  onClick={handleComplete}
                  disabled={loading}
                  className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 ${
                    loading
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-xl transform hover:scale-105"
                  }`}
                >
                  {loading ? "Setting up..." : "Complete Setup 🎉"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Onboarding;

